import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'mon-avatar',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './mon-avatar.component.html',
    styleUrl: './mon-avatar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonAvatarComponent implements OnChanges {
    @Input() name?: string;
    @Input() initials?: string;
    @Input() size = 40;
    @Input() bgColor?: string;
    @Input() textColor = '#ffffff';
    @Input() rounded = true;
    @Input() useAutoColor = false;
    @Input() maxInitialsSize = 2;

    displayInitials = '';

    // Color palette for auto colors
    private colors = [
        '#bdc3c7', '#6f7b87', '#2c3e50', '#2f3193', '#662d91',
        '#922790', '#ec2176', '#ed1c24', '#f36622', '#f8941e',
        '#fab70f', '#fdde00', '#d1d219', '#8ec73f', '#00a650',
        '#00aa9c', '#00adef', '#0081cd', '#005bab',
    ];

    ngOnChanges (changes: SimpleChanges): void {
        if (changes['initials'] || changes['name'] || changes['maxInitialsSize']) {
            this.updateDisplayInitials();
        }
    }

    updateDisplayInitials (): void {
        // If explicit initials are provided, use those
        if (this.initials) {
            this.displayInitials = this.initials.substring(0, this.maxInitialsSize);
        } else {
            // Otherwise generate from name
            this.generateInitials();
        }
    }

    generateInitials (): void {
        if (!this.name) {
            this.displayInitials = '';
            return;
        }

        const nameParts = this.name.split(' ').filter(n => n.length > 0);

        if (nameParts.length === 0) {
            this.displayInitials = '';
            return;
        }

        if (nameParts.length === 1 || this.maxInitialsSize === 1) {
            // Single name or max size 1
            this.displayInitials = nameParts[0].charAt(0).toUpperCase();
            return;
        }

        if (this.maxInitialsSize === 2 || nameParts.length === 2) {
            // For max size 2 or exactly 2 name parts
            const first = nameParts[0].charAt(0);
            const last = nameParts[nameParts.length - 1].charAt(0);
            this.displayInitials = (first + last).toUpperCase();
            return;
        }

        // For max size > 2 with multiple name parts
        let result = nameParts[0].charAt(0); // First initial

        // Add middle initials up to maxSize-1
        const middleCount = Math.min(this.maxInitialsSize - 2, nameParts.length - 2);
        for (let i = 0; i < middleCount; i++) {
            result += nameParts[i + 1].charAt(0);
        }

        // Add last initial
        result += nameParts[nameParts.length - 1].charAt(0);

        this.displayInitials = result.toUpperCase();
    }

    getBackgroundColor (): string {
        // If auto coloring is disabled or a specific background color is provided, use that
        if (!this.useAutoColor || (this.bgColor && this.bgColor !== '#3498db')) {
            return this.bgColor || '#3498db';
        }

        // Otherwise, generate a color based on name or initials
        const string = this.name || this.initials || '';
        let hash = 0;

        // Simple hash function to generate consistent colors
        for (let i = 0; i < string.length; i++) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Use the hash to pick a color from our palette
        const index = Math.abs(hash) % this.colors.length;
        return this.colors[index];
    }
}
